import {
  FaFacebookF,
  FaInstagram,
  FaLinkedinIn,
  FaTiktok,
  FaPhoneAlt,
  FaEnvelope,
  FaMapMarkerAlt,
} from "react-icons/fa";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";

const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  show: (i = 0) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.6, ease: "easeOut", delay: i * 0.08 },
  }),
};

const scaleIn = {
  hidden: { opacity: 0, scale: 0.96 },
  show: (i = 0) => ({
    opacity: 1,
    scale: 1,
    transition: { duration: 0.5, ease: "easeOut", delay: i * 0.08 },
  }),
};

function Footer() {
  return (
    <footer className="relative mt-20 overflow-hidden">
      {/* Light, catchy background */}
      <div className="absolute inset-0 bg-gradient-to-b from-sky-50 via-white to-amber-50" />
      {/* Soft decorative blobs */}
      <div className="pointer-events-none absolute -top-24 -right-24 h-72 w-72 rounded-full bg-sky-200/40 blur-3xl" />
      <div className="pointer-events-none absolute -bottom-28 -left-20 h-80 w-80 rounded-full bg-amber-200/40 blur-3xl" />

      <div className="relative max-w-7xl mx-auto px-6 py-16 text-slate-700">
        {/* Top bar: Brand + CTA */}
        <motion.div
          className="flex flex-col lg:flex-row items-start lg:items-center justify-between gap-8 pb-10"
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, amount: 0.2 }}
          variants={fadeUp}
        >
          <motion.div className="flex items-center gap-4" variants={fadeUp}>
            <div className="h-11 w-11 rounded-2xl bg-gradient-to-tr from-sky-500 to-cyan-400 shadow-lg grid place-items-center text-white font-bold">
              G
            </div>
            <div>
              <h3 className="text-2xl font-semibold text-slate-900">
                Glide Education &amp; Visa Services
              </h3>
              <p className="text-sm text-slate-600">
                Study pathways, admissions &amp; visa solutions—crafted for real outcomes.
              </p>
            </div>
          </motion.div>

          <motion.div variants={fadeUp}>
            <Link
              to="/contact"
              className="inline-flex items-center gap-2 rounded-full border border-sky-200 bg-white/80 px-5 py-2 text-sm text-sky-700 shadow-sm hover:shadow-md hover:bg-white transition"
            >
              Talk to a Consultation
              <span className="h-1.5 w-1.5 rounded-full bg-emerald-500 animate-pulse" />
            </Link>
          </motion.div>
        </motion.div>

        {/* Middle grid */}
        <div className="grid grid-cols-1 md:grid-cols-12 gap-10">
          {/* Offices (bright cards) */}
          <motion.div
            className="md:col-span-5 space-y-4"
            initial="hidden"
            whileInView="show"
            viewport={{ once: true, amount: 0.2 }}
          >
            <motion.h4 className="text-slate-900 font-semibold text-lg" variants={fadeUp}>
              Visit Us
            </motion.h4>

            {/* Nepal card */}
            <motion.div
              className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm hover:shadow-md transition"
              variants={scaleIn}
              custom={0}
            >
              <div className="flex items-center justify-between">
                <p className="text-sm font-medium text-slate-900">Nepal Office</p>
                <span className="text-[11px] px-2 py-0.5 rounded-full bg-sky-100 text-sky-700 border border-sky-200">
                  Kathmandu
                </span>
              </div>
              <div className="mt-3 space-y-2 text-sm">
                <p className="flex items-start gap-2">
                  <FaMapMarkerAlt className="mt-0.5 text-sky-500" />
                  Ghyasang Marg (Behind Old Buspark), Bagbazar, Kathmandu
                </p>
                <p className="flex items-center gap-2">
                  <FaPhoneAlt className="text-emerald-600" />
                  <a href="tel:+9779767959184" className="hover:text-slate-900">
                    +977 9767959184
                  </a>
                </p>
                <p className="flex items-center gap-2">
                  <FaEnvelope className="text-cyan-600" />
                  <a href="mailto:info@glideedu.com.au" className="hover:text-slate-900">
                    info@glideedu.com.au
                  </a>
                </p>
              </div>
            </motion.div>

            {/* Australia card */}
            <motion.div
              className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm hover:shadow-md transition"
              variants={scaleIn}
              custom={1}
            >
              <div className="flex items-center justify-between">
                <p className="text-sm font-medium text-slate-900">Australia Office</p>
                <span className="text-[11px] px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700 border border-emerald-200">
                  Adelaide
                </span>
              </div>
              <div className="mt-3 space-y-2 text-sm">
                <p className="flex items-start gap-2">
                  <FaMapMarkerAlt className="mt-0.5 text-sky-500" />
                  Level 8, Suite 8.23/30 Currie Street, Adelaide SA 5000
                </p>
                <p className="flex items-center gap-2">
                  <FaPhoneAlt className="text-emerald-600" />
                  <a href="tel:+61403883559" className="hover:text-slate-900">
                    +61 403 883 559
                  </a>
                </p>
                <p className="flex items-center gap-2">
                  <FaEnvelope className="text-cyan-600" />
                  <a href="mailto:info@glideedu.com.au" className="hover:text-slate-900">
                    info@glideedu.com.au
                  </a>
                </p>
              </div>
            </motion.div>
          </motion.div>

          {/* Services */}
          <motion.div
            className="md:col-span-4"
            initial="hidden"
            whileInView="show"
            viewport={{ once: true, amount: 0.2 }}
          >
            <motion.h4 className="text-slate-900 font-semibold text-lg mb-4" variants={fadeUp}>
              What We Do
            </motion.h4>
            <motion.ul
              className="text-sm rounded-2xl overflow-hidden border border-slate-200 bg-white"
              variants={fadeUp}
            >
              {[
                { to: "/services/education-consulting", label: "Education Consulting" },
                { to: "/services/visa-assistance", label: "Visa & Migration Services" },
                { to: "/services/skills-assessment", label: "Skills Assessment" },
                { to: "/services/health-insurance", label: "Health Insurance (OSHC/OVHC)" },
                { to: "/services/professional-year", label: "Professional Year" },
              ].map((item, i) => (
                <motion.li
                  key={item.to}
                  custom={i}
                  variants={fadeUp}
                  className={`px-4 py-3 hover:bg-sky-50 transition ${
                    i !== 0 ? "border-t border-slate-200" : ""
                  }`}
                >
                  <Link className="block" to={item.to}>
                    {item.label}
                  </Link>
                </motion.li>
              ))}
            </motion.ul>
          </motion.div>

          {/* Quick Links + Social chips */}
          <motion.div
            className="md:col-span-3"
            initial="hidden"
            whileInView="show"
            viewport={{ once: true, amount: 0.2 }}
          >
            <motion.h4 className="text-slate-900 font-semibold text-lg mb-4" variants={fadeUp}>
              Quick Links
            </motion.h4>
            <ul className="space-y-3 text-sm mb-6">
              {[
                { to: "/about", label: "About Us" },
                { to: "/services", label: "Our Services" },
                { to: "/news", label: "News & Blog" },
                { to: "/contact", label: "Contact" },
                {
                  href: "https://immi.homeaffairs.gov.au/visas/getting-a-visa/visa-finder",
                  label: "Visa Finder Tool",
                },
              ].map((item, i) =>
                item.to ? (
                  <motion.li key={item.label} custom={i} variants={fadeUp}>
                    <Link to={item.to} className="hover:text-slate-900">
                      {item.label}
                    </Link>
                  </motion.li>
                ) : (
                  <motion.li key={item.label} custom={i} variants={fadeUp}>
                    <a
                      href={item.href}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="hover:text-slate-900"
                    >
                      {item.label}
                    </a>
                  </motion.li>
                )
              )}
            </ul>

            <motion.h5 className="text-slate-900 font-medium text-sm mb-3" variants={fadeUp}>
              Follow Us
            </motion.h5>
            <div className="flex flex-wrap gap-3">
              {[
                { href: "https://www.linkedin.com/", label: "LinkedIn", Icon: FaLinkedinIn, ring: "ring-sky-300" },
                { href: "https://www.facebook.com/share/1CvJVAi2Kt/", label: "Facebook", Icon: FaFacebookF, ring: "ring-blue-300" },
                { href: "https://www.instagram.com/glideeduvisa?igsh=cGE5ZG14a2VwMm0z", label: "Instagram", Icon: FaInstagram, ring: "ring-pink-300" },
                { href: "https://www.tiktok.com/@glideeduvisanepal?_t=ZS-8z1CP1zsofH&_r=1", label: "TikTok", Icon: FaTiktok, ring: "ring-gray-300" },
              ].map(({ href, label, Icon, ring }, i) => (
                <motion.a
                  key={label}
                  href={href}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={label}
                  className={`group inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white px-3 py-2 text-xs text-slate-700 shadow-sm hover:shadow-md hover:-translate-y-0.5 transition ${ring} ring-1`}
                  initial={{ opacity: 0, y: 12 }}
                  whileInView={{ opacity: 1, y: 0, transition: { duration: 0.4, delay: i * 0.06 } }}
                  viewport={{ once: true, amount: 0.2 }}
                >
                  <span className="grid place-items-center h-6 w-6 rounded-full bg-slate-50 group-hover:bg-white">
                    <Icon className="text-slate-800" />
                  </span>
                  <span className="pr-1">{label}</span>
                </motion.a>
              ))}
            </div>
          </motion.div>
        </div>

        {/* Bottom bar */}
        <motion.div
          className="mt-12 flex flex-col sm:flex-row items-center justify-between gap-4 text-xs text-slate-500"
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, amount: 0.2 }}
          variants={fadeUp}
        >
          <p>
            &copy; {new Date().getFullYear()} Glide Education &amp; Visa Services. All rights reserved.
          </p>
          <p className="opacity-80">Crafted with care • Adelaide &amp; Kathmandu</p>
        </motion.div>
      </div>
    </footer>
  );
}

export default Footer;
